<script setup>
defineOptions({ name: "SolutionKaliAgroPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DocumentCheckIcon,
  CreditCardIcon,
  BanknotesIcon,
  UserGroupIcon,
  ArchiveBoxIcon,
  PuzzlePieceIcon,
  BoltIcon,
} from "@heroicons/vue/24/outline";
import {
  ScaleIcon,
  HomeModernIcon,
  SparklesIcon,
  BuildingStorefrontIcon,
  BriefcaseIcon,
  TruckIcon,
  TagIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kaliagro.webp";
import visaoGeralImg from "@/assets/visaogeralagro.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Agronegócio / Produtor rural" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Agro" (docs/books/KALI AGRO - EBOOK.pdf).
const segmentos = [
  { icon: ScaleIcon, label: "Grãos e cereais" },
  { icon: HomeModernIcon, label: "Pecuária de corte e leite" },
  { icon: SparklesIcon, label: "Hortifruti e fruticultura" },
  { icon: BuildingStorefrontIcon, label: "Avicultura e suinocultura" },
  { icon: UserGroupIcon, label: "Cooperativas e associações" },
  { icon: BriefcaseIcon, label: "Prestadores de serviço rural" },
  { icon: TruckIcon, label: "Logística e transporte de safra" },
  { icon: TagIcon, label: "Comercialização e revenda" },
];

const destaques = [
  {
    eyebrow: "Fiscal",
    title: "Notas fiscais em poucos cliques",
    text: "Emita NF-e, CT-e e MDF-e de forma automática, vinculada ao site da Receita Federal, direto da propriedade.",
  },
  {
    eyebrow: "Financeiro",
    title: "Controle total do caixa da propriedade",
    text: "Contas a pagar e receber, conciliação bancária e boletos integrados com o seu banco.",
  },
  {
    eyebrow: "Estoque",
    title: "Do insumo ao produto acabado",
    text: "Controle de produção, etiquetas personalizadas e curva ABC para saber o que mais rende.",
  },
  {
    eyebrow: "Vendas",
    title: "Orçamento e venda em um clique",
    text: "Envie orçamentos de forma inteligente e emita documentos fiscais junto com o pedido de venda.",
  },
];

const documentosFiscais = ["NF-e", "CT-e", "MDF-e"];

const controleTributario = [
  { label: "Livro Caixa Digital", shade: "bg-kali-blue-700" },
  { label: "Lucro Real", shade: "bg-kali-blue-600" },
  { label: "Lucro Presumido", shade: "bg-kali-blue-500" },
  { label: "Agro Fiscal (cooperativas)", shade: "bg-kali-blue-400" },
];

const modulos = [
  {
    icon: BanknotesIcon,
    title: "Controle financeiro",
    subtitle: "Controle total do seu caixa",
    items: [
      "Contas a pagar e receber",
      "Conciliação bancária",
      "Boletos integrados ao banco",
      "Orçamentos",
    ],
  },
  {
    icon: ArchiveBoxIcon,
    title: "Controle de estoque",
    subtitle: "Da matéria-prima ao produto acabado",
    items: [
      "Controle de produção",
      "Etiquetas personalizadas",
      "Curva ABC de produtos",
      "Relatórios de lucro",
    ],
  },
  {
    icon: HomeModernIcon,
    title: "Cadastro de imóvel rural",
    subtitle: "Gerencie propriedades e talhões",
    items: [
      "CAFIR e CAEPF",
      "Cadastro de proprietários",
      "Tipo de exploração",
      "Valor da área (HA)",
    ],
  },
  {
    icon: DocumentCheckIcon,
    title: "Agro Fiscal",
    subtitle: "Ideal para cooperativas",
    items: [
      "Cadastro de vários produtores rurais",
      "Emissão de notas independente",
      "Controle total das emissões",
    ],
  },
  {
    icon: CreditCardIcon,
    title: "Controle de vendas",
    subtitle: "Da cotação ao faturamento",
    items: [
      "Pedido de venda",
      "Orçamentos inteligentes",
      "Documentos fiscais em um clique",
    ],
  },
  {
    icon: PuzzlePieceIcon,
    title: "Soluções integradas",
    subtitle: "Tudo conectado ao sistema",
    items: [
      "Assinador digital",
      "Averbação em notas",
      "Boleto automático",
      "WhatsApp",
    ],
  },
  {
    icon: DevicePhoneMobileIcon,
    title: "SEUPOS",
    subtitle: "App de vendas online e offline",
    items: [
      "Compatível com Android e iOS",
      "Controle o PDV de onde estiver",
      "Funciona no campo, sem internet",
    ],
  },
  {
    icon: BoltIcon,
    title: "CONNECT",
    subtitle: "Pagamento sem TEF tradicional",
    items: [
      "Integração Stone, Cielo e Vero",
      "Integração Mercado Pago",
      "Mais ágil e mais barato que o TEF",
    ],
  },
];

useSeo({
  title: "Sistema de Gestão para Produtores Rurais e Agronegócio | Kali Agro",
  description:
    "Controle fiscal, financeiro e produção com o Kali Agro. Sistema completo para propriedades rurais, cooperativas e produtores. Teste grátis!",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Agro",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-agro",
    description:
      "Sistema de gestão completo para produtores rurais, cooperativas e agronegócios, com controle fiscal, financeiro, de estoque e produção em um só lugar.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Agro: Sistema de Gestão para Produtores Rurais, Cooperativas e Agronegócios"
      description="Controle fiscal, financeiro, estoque e produção com um sistema simples, completo e 100% online. Ideal para quem cultiva, cria ou transforma no campo."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-agro"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Agro"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para o produtor rural brasileiro
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Ninguém conhece mais a sua terra do que você. O Kali Agro ajuda a
            controlar a produção e otimizar os resultados no campo — da
            emissão de notas fiscais ao manejo do cultivo ou do rebanho,
            gerenciando sua propriedade de qualquer lugar.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Soluções que atendem à realidade de cada
            <span class="text-kali-blue-600">produtor</span>
            e operação
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Agro atende diferentes atividades do campo, se adaptando à
            realidade operacional de cada propriedade, cooperativa ou
            produtor rural.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Atendemos produtores de todo o Brasil
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Tudo que a sua propriedade precisa, em um só sistema
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da emissão fiscal ao controle da safra, cada funcionalidade foi
            pensada para tirar trabalho manual do seu dia.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <div class="flex justify-center items-center w-full h-44 rounded-2xl bg-gradient-to-br from-kali-blue-100 to-kali-blue-200"></div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Agro acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua propriedade em conformidade sem parar
              de produzir.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, de forma
              automática e vinculada ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
            <ul class="mt-6 space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Entrada de Notas —</span>
                de forma automática, vinculado ao site da Receita Federal.
              </li>
            </ul>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Chega de acumular papéis. Gerencie sua propriedade de forma
              integrada com o Livro Caixa Digital, para Pessoa Física (CPF),
              Lucro Real e Lucro Presumido.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
          Módulos do Kali Agro
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Agro a módulos adicionais e leve a gestão da sua
          propriedade a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'O sistema atende diferentes culturas e atividades?',
            answer:
              'Sim. Você pode configurar produtos, insumos e atividades de acordo com sua realidade (grãos, hortifruti, pecuária etc.).',
          },
          {
            question: 'Consigo controlar custos por talhão ou lote?',
            answer:
              'Sim. Registre custos por área, lote ou atividade e obtenha relatórios de rentabilidade.',
          },
          {
            question: 'O sistema funciona em áreas sem internet?',
            answer:
              'Sim. Com o app de vendas/registro offline (SEUPOS), você opera e sincroniza quando houver conexão.',
          },
          {
            question: 'Emite notas fiscais para produtores?',
            answer:
              'Sim. NF-e, NFC-e e NFS-e conforme a legislação vigente, com integração contábil.',
          },
        ]"
        description="Dúvidas sobre Kali Agro? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
